import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  useConfirmBillingMigrationMutation,
  useDeleteBillingMigrationSessionMutation,
  useGetBillingMigrationSessionQuery,
} from '../store/billing-migration-api';
import {
  type BillingMigrationImportStats,
  type BillingMigrationPreviewData,
  formatCents,
  getPollingInterval,
  getStatusColor,
  getStatusLabel,
  isSessionProcessing,
} from '../utils/billing-migration-status-helpers';

const StatCard = ({
  label,
  value,
  color = 'grey',
}: {
  label: string;
  value: string | number;
  color?: string;
}) => (
  <Card variant="outlined" sx={{ backgroundColor: `${color}.50` }}>
    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
    </CardContent>
  </Card>
);

export default function BillingMigrationSessionDetail() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [showErrors, setShowErrors] = useState(false);

  const {
    data: session,
    isLoading,
    isError,
    refetch,
  } = useGetBillingMigrationSessionQuery(
    { sessionId: sessionId ?? '' },
    { skip: !sessionId },
  );

  const [confirmImport, { isLoading: isConfirming }] = useConfirmBillingMigrationMutation();
  const [deleteSession, { isLoading: isDeleting }] = useDeleteBillingMigrationSessionMutation();

  // Polling
  useEffect(() => {
    if (!session?.status) return;
    const interval = getPollingInterval(session.status);
    if (!interval) return;

    const id = setInterval(() => void refetch(), interval);
    return () => clearInterval(id);
  }, [session?.status, refetch]);

  const handleBack = useCallback(() => {
    navigate('/practice/billing-settings?tab=data-migration');
  }, [navigate]);

  const handleConfirm = useCallback(async () => {
    if (!sessionId) return;
    try {
      await confirmImport({ sessionId }).unwrap();
    } catch {
      // Error will be reflected in session status on next poll
    }
  }, [sessionId, confirmImport]);

  const handleDelete = useCallback(async () => {
    if (!sessionId) return;
    try {
      await deleteSession({ sessionId }).unwrap();
      handleBack();
    } catch {
      // Error handling via polling
    }
  }, [sessionId, deleteSession, handleBack]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !session) {
    return (
      <Container sx={{py: 3 }} maxWidth={"xl"}>
        <Button onClick={handleBack} sx={{ mb: 2 }}>
          ← Back to Data Migration
        </Button>
        <Alert severity="error">Failed to load session details.</Alert>
      </Container>
    );
  }

  const stats = session.import_stats as BillingMigrationImportStats | undefined;
  const preview = session.preview_data as BillingMigrationPreviewData | null | undefined;
  const errors = session.error_messages as Array<{ message: string; timestamp?: string }> | undefined;
  const processing = isSessionProcessing(session.status);
  const isPreviewReady = session.status === 'preview_ready';
  const isCompleted = session.status === 'completed';
  const isTerminal = isCompleted || session.status === 'failed' || session.status === 'cancelled';

  return (
    <Container sx={{py: 3 }} maxWidth={"xl"}>
      <Button onClick={handleBack} sx={{ mb: 2 }}>
        ← Back to Data Migration
      </Button>

      {/* Session Info */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
            <Typography variant="h5">Migration Session</Typography>
            <Chip
              label={getStatusLabel(session.status)}
              color={getStatusColor(session.status)}
              size="small"
              variant="outlined"
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Source: <strong style={{ textTransform: 'capitalize' }}>{session.source_system}</strong>
            {' · '}
            Created: {session.created_at ? new Date(session.created_at).toLocaleString() : '—'}
            {session.completed_at && (
              <>
                {' · '}
                Completed: {new Date(session.completed_at).toLocaleString()}
              </>
            )}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => void refetch()} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {isPreviewReady && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircleIcon />}
              onClick={handleConfirm}
              disabled={isConfirming}
            >
              {isConfirming ? 'Confirming...' : 'Confirm Import'}
            </Button>
          )}

          {!isTerminal && !processing && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={isDeleting}
            >
              Cancel
            </Button>
          )}
        </Box>
      </Box>

      {/* Processing indicator */}
      {processing && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {session.status === 'parsing'
            ? 'Parsing uploaded files... This may take a moment.'
            : session.status === 'importing'
              ? 'Importing records... This may take a moment.'
              : 'Processing...'}
        </Alert>
      )}

      {/* File Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard label="Total Files" value={stats.files_total ?? '—'} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard label="837 (Claims)" value={stats.files_837 ?? '—'} color="blue" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard label="835 (ERAs)" value={stats.files_835 ?? '—'} color="green" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              label="File Errors"
              value={stats.files_error ?? 0}
              color={stats.files_error ? 'red' : 'grey'}
            />
          </Grid>
        </Grid>
      )}

      {/* Import Results (post-confirm) */}
      {isCompleted && stats && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Import Results
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <StatCard label="Claims Created" value={stats.claims_created ?? 0} color="green" />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <StatCard label="Claims Skipped" value={stats.claims_skipped ?? 0} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <StatCard label="Remittances Created" value={stats.remittances_created ?? 0} color="green" />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <StatCard label="Remittances Skipped" value={stats.remittances_skipped ?? 0} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <StatCard label="ERA↔Claim Matches" value={stats.remittance_claims_matched ?? 0} color="blue" />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <StatCard
                  label="Errors"
                  value={(stats.claim_errors ?? 0) + (stats.remittance_errors ?? 0)}
                  color={(stats.claim_errors ?? 0) + (stats.remittance_errors ?? 0) > 0 ? 'red' : 'grey'}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Preview Data */}
      {isPreviewReady && preview && (
        <>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Preview Summary</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 4, md: 'grow' }}><StatCard label="Total Claims" value={preview.summary.total_claims} color="blue" /></Grid>
                <Grid size={{ xs: 6, sm: 4, md: 'grow' }}><StatCard label="Total ERAs" value={preview.summary.total_eras} color="green" /></Grid>
                <Grid size={{ xs: 6, sm: 4, md: 'grow' }}><StatCard label="ERA Line Items" value={preview.summary.total_era_claims} /></Grid>
                <Grid size={{ xs: 6, sm: 4, md: 'grow' }}><StatCard label="Unique NPIs" value={preview.summary.unique_npis} /></Grid>
                <Grid size={{ xs: 6, sm: 4, md: 'grow' }}><StatCard label="Unique Patients" value={preview.summary.unique_patients} /></Grid>
              </Grid>
            </CardContent>
          </Card>

          {(preview.providers.billing_npis.length > 0 || preview.providers.rendering_npis.length > 0) && (
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Provider NPIs Found</Typography>
                {preview.providers.billing_npis.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">Billing NPIs:</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {preview.providers.billing_npis.map(npi => <Chip key={npi} label={npi} size="small" variant="outlined" />)}
                    </Box>
                  </Box>
                )}
                {preview.providers.rendering_npis.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Rendering NPIs:</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {preview.providers.rendering_npis.map(npi => <Chip key={npi} label={npi} size="small" variant="outlined" />)}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {preview.sample_claims.length > 0 && (
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Sample Claims</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>PCN</TableCell>
                        <TableCell>Patient</TableCell>
                        <TableCell>Payer</TableCell>
                        <TableCell>CPT Codes</TableCell>
                        <TableCell>Service Date</TableCell>
                        <TableCell align="right">Charge</TableCell>
                        <TableCell>Rendering NPI</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.sample_claims.map((claim, i) => (
                        <TableRow key={i}>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{claim.pcn}</Typography></TableCell>
                          <TableCell>{claim.patient}</TableCell>
                          <TableCell>{claim.payer}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {claim.cpt_codes.map(code => <Chip key={code} label={code} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />)}
                            </Box>
                          </TableCell>
                          <TableCell>{claim.service_date}</TableCell>
                          <TableCell align="right">{formatCents(claim.charge_cents)}</TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{claim.rendering_npi}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {preview.sample_eras.length > 0 && (
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Sample ERAs</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Trace Number</TableCell>
                        <TableCell>Payer</TableCell>
                        <TableCell align="right">Payment</TableCell>
                        <TableCell align="right">Claims</TableCell>
                        <TableCell>Sample PCNs</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.sample_eras.map((era, i) => (
                        <TableRow key={i}>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{era.trace_number}</Typography></TableCell>
                          <TableCell>{era.payer}</TableCell>
                          <TableCell align="right">{formatCents(era.payment_cents)}</TableCell>
                          <TableCell align="right">{era.claim_count}</TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{era.sample_pcns.join(', ')}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Error Messages */}
      {errors && errors.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => setShowErrors(prev => !prev)}
            >
              <Typography variant="subtitle1" color="error">
                Errors ({errors.length})
              </Typography>
            </Box>
            <Collapse in={showErrors}>
              <Divider sx={{ my: 1 }} />
              {errors.map((err, i) => (
                <Alert key={i} severity="error" sx={{ mb: 1 }}>
                  {err.message}
                  {err.timestamp && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {new Date(err.timestamp).toLocaleString()}
                    </Typography>
                  )}
                </Alert>
              ))}
            </Collapse>
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
